import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSubmitCSATMutation } from '@features/feedback';
import { useEventTracking } from '@services/statsig';

import { DEFAULT_NEGATIVE_CATEGORIES, THANKS_DISMISS_MS } from '../constants';
import type {
  ThumbsFeedbackContainerProps,
  ThumbsFeedbackPresentationalProps,
  ThumbsFeedbackStep,
  ThumbsFeedbackValue,
} from '../types';

/**
 * Business logic hook for the thumbs feedback widget.
 *
 * Manages the three-tier state machine (idle → categories → submitted → hidden)
 * and calls the existing CSAT endpoint for persistence.
 */
export function useThumbsFeedback(props: ThumbsFeedbackContainerProps): ThumbsFeedbackPresentationalProps {
  const {
    contentType,
    responseId,
    mode = 'thumbs-only',
    expandMode = 'inline',
    modelVersion,
    categories: customCategories,
    contextIds,
    compact = false,
    thanksDismissMs = THANKS_DISMISS_MS,
    thanksMessage: customThanksMessage,
    onFeedbackSubmitted,
  } = props;

  const feedbackSessionId = useMemo(() => crypto.randomUUID(), []);
  const categories = customCategories ?? DEFAULT_NEGATIVE_CATEGORIES;
  const thanksMessage = customThanksMessage ?? (compact ? 'Thanks' : 'Thanks for your feedback');

  // State
  const [step, setStep] = useState<ThumbsFeedbackStep>('idle');
  const [sentiment, setSentiment] = useState<ThumbsFeedbackValue | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  // API + tracking
  const [submitCSAT, { isLoading: isSubmitting }] = useSubmitCSATMutation();
  const { thumbsFeedback: track } = useEventTracking();

  // Prevent double-submission
  const submittedRef = useRef(false);

  // Auto-hide "Thanks" after configurable delay
  useEffect(() => {
    if (step !== 'submitted' || thanksDismissMs === 0) return;
    const timer = setTimeout(() => setStep('hidden'), thanksDismissMs);
    return () => clearTimeout(timer);
  }, [step, thanksDismissMs]);

  const thanksThenDone = useCallback(() => {
    setStep('submitted');
  }, []);

  const submitScore = useCallback(
    async (value: ThumbsFeedbackValue) => {
      await submitCSAT({
        csatRequest: {
          feedback: {
            feedback_type: 'score',
            feedback_session_id: feedbackSessionId,
            related_entity_type: contentType,
            related_entity_id: responseId,
            score: value === 'positive' ? 1 : 0,
          },
        },
      }).unwrap();
    },
    [submitCSAT, feedbackSessionId, contentType, responseId],
  );

  const handleThumbsUp = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    setSentiment('positive');
    track.positive({
      content_type: contentType,
      response_id: responseId,
      model_version: modelVersion,
      ...contextIds,
    });

    try {
      await submitScore('positive');
    } catch {
      // Score submission failed — still show thanks to user, error tracked by RTK
      submittedRef.current = false;
    }

    // Fires on user intent, not API success — callers should treat this as
    // "user submitted feedback" not "feedback was persisted". The UX shows
    // thanks regardless to avoid penalizing users for transient API failures.
    onFeedbackSubmitted?.('positive');
    thanksThenDone();
  }, [contentType, responseId, modelVersion, contextIds, track, submitScore, onFeedbackSubmitted, thanksThenDone]);

  const handleThumbsDown = useCallback(async () => {
    if (submittedRef.current) return;

    setSentiment('negative');
    track.negative({
      content_type: contentType,
      response_id: responseId,
      model_version: modelVersion,
      ...contextIds,
    });

    // Fire score immediately
    try {
      await submitScore('negative');
    } catch {
      // Continue to category selection even if score fails
    }

    // In thumbs-only mode, we're done
    if (mode === 'thumbs-only') {
      submittedRef.current = true;
      onFeedbackSubmitted?.('negative');
      thanksThenDone();
      return;
    }

    // Expand to categories
    setStep('categories');
  }, [contentType, responseId, modelVersion, contextIds, mode, track, submitScore, onFeedbackSubmitted, thanksThenDone]);

  const handleCategoryToggle = useCallback(
    (id: string) => {
      if (mode === 'categories') {
        // Categories-only mode: single select → auto-submit immediately
        setSelectedCategories([id]);

        // Fire submission inline (can't call handleSubmitDetails because of stale closure)
        if (submittedRef.current) return;
        submittedRef.current = true;

        track.categoriesSubmitted({
          content_type: contentType,
          response_id: responseId,
          categories: id,
          comment_length: '0',
        });

        submitCSAT({
          csatRequest: {
            feedback: {
              feedback_type: 'details',
              feedback_session_id: feedbackSessionId,
              related_entity_type: contentType,
              related_entity_id: responseId,
              improvement_areas: [id],
              feedback_comment: null,
              other_area: null,
            },
          },
        })
          .unwrap()
          .catch(() => {
            submittedRef.current = false;
          });

        onFeedbackSubmitted?.('negative');
        thanksThenDone();
        return;
      }

      // categories-comment mode: multi-select toggle, manual submit
      setSelectedCategories(prev =>
        prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id],
      );
    },
    [
      mode,
      contentType,
      responseId,
      feedbackSessionId,
      track,
      submitCSAT,
      onFeedbackSubmitted,
      thanksThenDone,
    ],
  );

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
  }, []);

  const handleSubmitDetails = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    // Track categories submission
    track.categoriesSubmitted({
      content_type: contentType,
      response_id: responseId,
      categories: selectedCategories.join(','),
      comment_length: String(comment.length),
    });

    try {
      await submitCSAT({
        csatRequest: {
          feedback: {
            feedback_type: 'details',
            feedback_session_id: feedbackSessionId,
            related_entity_type: contentType,
            related_entity_id: responseId,
            improvement_areas: selectedCategories.length > 0 ? selectedCategories : null,
            feedback_comment: comment || null,
            other_area: null,
          },
        },
      }).unwrap();
    } catch {
      // Details submission failed — still show thanks
      submittedRef.current = false;
    }

    onFeedbackSubmitted?.('negative');
    thanksThenDone();
  }, [
    selectedCategories,
    comment,
    submitCSAT,
    feedbackSessionId,
    contentType,
    responseId,
    track,
    onFeedbackSubmitted,
    thanksThenDone,
  ]);

  const handleDismiss = useCallback(() => {
    track.dismissed({
      content_type: contentType,
      response_id: responseId,
    });
    setStep('idle');
    setSentiment(null);
    setSelectedCategories([]);
    setComment('');
    submittedRef.current = false;
  }, [track, contentType, responseId]);

  return {
    step,
    sentiment,
    mode,
    expandMode,
    categories,
    selectedCategories,
    comment,
    compact,
    thanksMessage,
    isSubmitting,
    onThumbsUp: handleThumbsUp,
    onThumbsDown: handleThumbsDown,
    onCategoryToggle: handleCategoryToggle,
    onCommentChange: handleCommentChange,
    onSubmitDetails: handleSubmitDetails,
    onDismiss: handleDismiss,
  };
}
